// Чистая часть `useBlockDraft`: какой серверный блок сейчас «тот самый» и как
// черновик синхронизируется с ним. «Тот же блок» — по живой идентичности
// (`originId ?? id`), а не по id: смена источника чтения (черновик ⇄ живой курс)
// меняет id той же сущности, и черновик автора обязан это пережить.
import type { Block } from '@/types/blocks'
import { resolveAlias, type IdAliases } from '@/features/editor/lib/idAliases'
import { blockSignature } from '@/features/editor/lib/blockSignature'
import { liveIdentity } from '@/features/moderation/lib/liveIdentity'
import { carryDiff } from './carryDiff'

/**
 * Серверный блок выделения. Выделение могло уже переехать на id черновика
 * (сохранение живого блока вернуло блок черновика), а список урока ещё из
 * прежнего источника: в нём лежит тот же блок под старым id (алиас ведёт в
 * выделенный). Такой список отстал, и сравнивать черновик с ним нельзя — это
 * тело ДО сохранения. До перечитывания курса серверным блоком служит последний
 * увиденный снимок (`lastSeen` — ответ того самого сохранения).
 */
/**
 * Блок списка — это выделенный блок, даже если список отстал. Первая правка
 * живого курса заводит черновик: выделение переезжает на новый id сразу, а
 * список урока ещё живой (старые id) до перечитывания курса. Без перевода
 * через алиасы холст показывал «Выберите блок», а миниатюра гасла, хотя панель
 * справа — на блоке.
 */
export const isSameBlock = (aliases: IdAliases, listId: number, selectedId: number | null) =>
  selectedId !== null &&
  (listId === selectedId || resolveAlias(aliases, 'block', listId) === selectedId)

export const pickServerBlock = (
  blocks: readonly Block[],
  selectedId: number | null,
  aliases: IdAliases,
  lastSeen: Block | null,
): Block | null => {
  if (selectedId === null) return null
  const found = blocks.find((b) => b.id === selectedId)
  if (found) return found
  const listIsStale = blocks.some(
    (b) => b.id !== selectedId && resolveAlias(aliases, 'block', b.id) === selectedId,
  )
  return listIsStale && lastSeen?.id === selectedId ? lastSeen : null
}

export interface DraftSync {
  draft: Block | null
  /** Новая основа черновика (`lastServerBlockRef`). */
  base: Block | null
}

export const syncDraft = (
  prev: Block | null,
  block: Block | null,
  lastSeen: Block | null,
): DraftSync => {
  if (!block) return { draft: null, base: null }
  if (!prev || liveIdentity(prev) !== liveIdentity(block)) return { draft: block, base: block }
  // Черновик совпадает с последним снимком — автор с тех пор не правил, новое
  // серверное тело (перепись прогоном, смена источника) принимается целиком.
  const untouched = lastSeen !== null && blockSignature(prev) === blockSignature(lastSeen)
  if (untouched) return { draft: block, base: block }
  // Правка автора (разница с основой, `carryDiff`) переезжает на блок нового
  // источника; он же становится основой — с ним дальше сравниваются конфликт и история.
  // Смена основы намеренно обнуляет историю undo (`useDraftHistory` ключуется основой).
  if (prev.id !== block.id) return { draft: carryDiff(prev, lastSeen, block), base: block }
  // Настоящая несохранённая правка: только `order` серверный и не должен протухнуть.
  const draft = prev.order === block.order ? prev : { ...prev, order: block.order }
  return { draft, base: lastSeen }
}

/**
 * Эхо своего сохранения (рефетч после PUT): серверный блок становится основой,
 * авторский черновик остаётся как есть — нетронутый принимает блок целиком,
 * допечатанный во время запроса уходит следующим автосохранением.
 */
export const acceptEcho = (prev: Block, block: Block, base: Block): DraftSync => {
  if (prev.id !== block.id || blockSignature(prev) === blockSignature(base)) {
    return { draft: block, base: block }
  }
  const draft = prev.order === block.order ? prev : { ...prev, order: block.order }
  return { draft, base: block }
}
